import { selectProductCardColor, toggleProductCardImage } from '../ProductCard';

export const listingEventsMethods = {
    onRootClick(event) {
        const filtersOpenTrigger = event.target.closest(
            '[data-listing-filters-open]'
        );

        if (filtersOpenTrigger) {
            event.preventDefault();
            this.syncPendingFiltersFromState();
            this.filtersSidebar?.open();

            if (!this.filterCountsLoaded) {
                this.loadFilterTermCounts();
            }

            this.refreshFilterCollapseUi();
            return;
        }

        const filtersApplyTrigger = event.target.closest(
            '[data-listing-filters-apply]'
        );

        if (filtersApplyTrigger) {
            event.preventDefault();

            if (this.pendingFiltersCountTimer) {
                window.clearTimeout(this.pendingFiltersCountTimer);
                this.pendingFiltersCountTimer = null;
                this.applyPendingSidebarToState();
                this.fetchAndRender({ pushUrl: this.getListingUrl() });
            }

            this.filtersSidebar?.close();
            return;
        }

        const filtersClearTrigger = event.target.closest(
            '[data-listing-filters-clear]'
        );

        if (filtersClearTrigger) {
            event.preventDefault();
            event.stopPropagation();

            const isSidebarClear = Boolean(
                this.filtersSidebar?.content?.contains(filtersClearTrigger)
            );

            if (isSidebarClear) {
                this.clearSidebarFilters({ applyToListing: true });
                return;
            }

            this.clearAppliedListingFilters();
            return;
        }

        const activeFilterRemoveTrigger = event.target.closest(
            '[data-listing-active-filter-remove]'
        );

        if (activeFilterRemoveTrigger) {
            event.preventDefault();

            const filterKey = activeFilterRemoveTrigger.dataset.filterKey || '';
            const filterValue =
                activeFilterRemoveTrigger.dataset.filterValue || '';

            if (!filterKey || !filterValue) {
                return;
            }

            if (filterKey === 'price_min') {
                this.state.priceMin = null;
                this.state.page = 1;
                this.syncPendingFiltersFromState();
                this.fetchAndRender({ pushUrl: this.getListingUrl() });
                return;
            }

            if (filterKey === 'price_max') {
                this.state.priceMax = null;
                this.state.page = 1;
                this.syncPendingFiltersFromState();
                this.fetchAndRender({ pushUrl: this.getListingUrl() });
                return;
            }

            if (filterKey === 'sort') {
                this.state.sort = 'popular';
                this.state.page = 1;
                this.updateSortUi('popular');
                this.syncPendingFiltersFromState();
                this.fetchAndRender({ pushUrl: this.getListingUrl() });
                return;
            }

            const nextFilters = this.cloneFilters(this.state.filters);
            const currentValue = nextFilters[filterKey];

            if (Array.isArray(currentValue)) {
                const nextValues = currentValue.filter(
                    value => value !== filterValue
                );

                if (nextValues.length > 0) {
                    nextFilters[filterKey] = nextValues;
                } else {
                    delete nextFilters[filterKey];
                }
            } else if (currentValue === filterValue) {
                delete nextFilters[filterKey];
            }

            this.state.filters = nextFilters;
            this.state.page = 1;
            this.syncPendingFiltersFromState();
            this.fetchAndRender({ pushUrl: this.getListingUrl() });
            return;
        }

        const filterMoreTrigger = event.target.closest(
            '[data-listing-filter-more]'
        );

        if (filterMoreTrigger) {
            event.preventDefault();
            event.stopPropagation();
            this.expandFilterCollapseGroup(
                filterMoreTrigger.closest('[data-listing-filter-collapsible]')
            );
            return;
        }

        const filterToggle = event.target.closest(
            '[data-listing-filter-toggle]'
        );

        if (filterToggle) {
            event.preventDefault();

            const node = filterToggle.closest(
                '[data-listing-filter-group], [data-listing-filter-node]'
            );
            if (!node) {
                return;
            }

            this.toggleListingFilterAccordion(node);
            return;
        }

        const filterSelect = event.target.closest(
            '[data-listing-filter-select]'
        );

        if (filterSelect) {
            event.preventDefault();

            const taxonomy =
                filterSelect.dataset.taxonomy || this.state.taxonomy;
            const slug = filterSelect.dataset.termSlug || '';
            const usesBranchSelection =
                this.usesHierarchicalFilterSelection(taxonomy);
            const node = filterSelect.closest('[data-listing-filter-node]');

            if (!taxonomy || !slug) {
                return;
            }

            if (usesBranchSelection && !node) {
                return;
            }

            const selectedSlugs = new Set(this.pendingFilters[taxonomy] || []);
            const isSelected = this.isSlugCoveredByPendingSelection(
                taxonomy,
                slug
            );
            const nodeSlugs = node
                ? this.getFilterNodeSlugs(node)
                : [slug];
            const descendantsMap =
                this.bootstrap.termDescendants?.[taxonomy] || {};

            if (isSelected) {
                if (usesBranchSelection) {
                    nodeSlugs.forEach(item => selectedSlugs.delete(item));

                    Array.from(selectedSlugs).forEach(selectedSlug => {
                        const branch = descendantsMap[selectedSlug] || [];

                        if (branch.includes(slug)) {
                            selectedSlugs.delete(selectedSlug);
                        }
                    });
                } else {
                    selectedSlugs.delete(slug);
                }
            } else if (usesBranchSelection) {
                nodeSlugs.forEach(item => selectedSlugs.add(item));
            } else {
                selectedSlugs.add(slug);
            }

            this.pendingFilters[taxonomy] = this.sortFilterValuesByDomOrder(
                taxonomy,
                Array.from(selectedSlugs)
            );

            this.updatePendingFilterSelectionUi();
            this.schedulePendingFiltersCountPreview();
            return;
        }

        const sortToggle = event.target.closest('[data-listing-sort-toggle]');

        if (sortToggle) {
            event.preventDefault();
            this.toggleSortMenu();
            return;
        }

        const sortOption = event.target.closest('[data-listing-sort-option]');

        if (sortOption) {
            event.preventDefault();

            const nextSort = sortOption.dataset.sortValue || 'popular';
            const isSidebarSortOption = Boolean(
                this.filtersSidebar?.content?.contains(sortOption)
            );

            if (isSidebarSortOption) {
                this.applyListingSort(nextSort, {
                    fetch: true,
                    pushUrl: true,
                    closeSidebar: false,
                });
                return;
            }

            if (nextSort !== this.state.sort) {
                this.applyListingSort(nextSort, {
                    fetch: true,
                    pushUrl: true,
                    closeSidebar: false,
                });
            } else {
                this.updateSortUi(nextSort);
            }

            this.closeSortMenu();
            return;
        }

        const colorButton = event.target.closest('[js-product-card-color]');

        if (colorButton) {
            event.preventDefault();
            event.stopPropagation();
            selectProductCardColor(colorButton);

            return;
        }

        const switchImageButton = event.target.closest('[js-switch-image]');

        if (switchImageButton) {
            event.preventDefault();
            event.stopPropagation();
            toggleProductCardImage(switchImageButton);

            return;
        }

        const loadMoreTrigger =
            event.target.closest('[data-listing-load-more] button') ||
            event.target.closest('[data-listing-load-more]');

        if (loadMoreTrigger) {
            event.preventDefault();

            const maxPages = Number(
                this.resultsPagination?.dataset.maxPages || 1
            );
            const currentPage = Number(
                this.resultsPagination?.dataset.page || this.state.page || 1
            );

            if (currentPage >= maxPages) {
                return;
            }

            this.state.page = currentPage + 1;
            this.fetchAndRender({ appendResults: true, showLoader: true });
            return;
        }

        const termsScrollTrigger = event.target.closest(
            '[data-listing-terms-prev], [data-listing-terms-next]'
        );

        if (termsScrollTrigger) {
            event.preventDefault();

            const carousel = termsScrollTrigger.closest(
                '[data-listing-terms-carousel]'
            );

            this.scrollTermsTrack(
                carousel,
                termsScrollTrigger.hasAttribute('data-listing-terms-prev')
                    ? -1
                    : 1
            );
            return;
        }

        const link = event.target.closest(
            '[data-listing-term], [data-listing-term-back]'
        );

        if (!link) {
            return;
        }

        return;
    },

    onDocumentClick(event) {
        if (!this.sortRoot || this.sortRoot.contains(event.target)) {
            return;
        }

        this.closeSortMenu();
    },

    onPopState(event) {
        const nextState = event.state?.listingState;

        if (!nextState) {
            window.location.reload();
            return;
        }

        this.state = {
            ...nextState,
            priceMin:
                nextState.priceMin !== undefined && nextState.priceMin !== null
                    ? this.normalizePriceValue(nextState.priceMin)
                    : null,
            priceMax:
                nextState.priceMax !== undefined && nextState.priceMax !== null
                    ? this.normalizePriceValue(nextState.priceMax)
                    : null,
        };
        this.fetchAndRender({ pushUrl: null });
    },
};
